import React, { useState, useEffect, useCallback } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import AdminHeader from "../components/AdminHeader";
import { listMembers, addMember, deleteMember, updateMember, importMembers, listEventRegistrations } from "../lib/membership";
import { WeeklyAttendance } from "../components/WeeklyAttendance";
import { HALLOWEEN } from "../lib/events";
import { UserPlus, Users, Clock, QrCode, Trash2, Search, Printer, X, Save, AlertTriangle, MapPin, ChevronRight, Ghost } from "lucide-react";

const TABS = [
  { id: "signup", label: "SIGN UP", icon: UserPlus },
  { id: "directory", label: "DIRECTORY", icon: Users },
  { id: "attendance", label: "WHO'S IN", icon: Clock },
  { id: "events", label: "RAVE LIST", icon: Ghost },
  { id: "qr", label: "DOOR QR", icon: QrCode },
];

export default function Memberships() {
  const [tab, setTab] = useState("signup");
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ name: "", dob: "", email: "", phone: "", parentPhone: "" });
  const [lastAdded, setLastAdded] = useState(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [edit, setEdit] = useState(null);

  const openMember = (m) => { setSelected(m); setEdit({ ...m, parentPhone: m.parent_phone || "", address: m.address || "", strikes: m.strikes || 0 }); };
  const closeMember = () => { setSelected(null); setEdit(null); };
  const setStrikes = (n) => setEdit((p) => ({ ...p, strikes: Math.max(0, Math.min(2, n)) }));
  const saveMember = async () => {
    try {
      await updateMember(selected.id, {
        name: edit.name, dob: edit.dob, email: edit.email, phone: edit.phone,
        parent_phone: edit.parentPhone || "", address: edit.address || "", strikes: edit.strikes || 0,
      });
      toast.success("Membership updated");
      closeMember();
      await load();
    } catch (e) {
      toast.error("Could not save changes");
    }
  };

  const [eventRegs, setEventRegs] = useState([]);

  const load = useCallback(async () => {
    try {
      const [m, r] = await Promise.all([listMembers(), listEventRegistrations(HALLOWEEN.slug)]);
      setMembers(m);
      setEventRegs(r);
    } catch (e) {
      toast.error("Could not load memberships");
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const localMembers = JSON.parse(localStorage.getItem("yaz_members") || "[]");
        const localAttendance = JSON.parse(localStorage.getItem("yaz_attendance") || "[]");
        if (localMembers.length && localStorage.getItem("yaz_migrated") !== "1") {
          const res = await importMembers({ members: localMembers, attendance: localAttendance });
          localStorage.setItem("yaz_migrated", "1"); // keep the local backup, just don't re-import
          if (res.imported_members > 0) {
            toast.success(`Restored ${res.imported_members} membership(s) from this device`);
          }
        }
      } catch (e) {
        // ignore migration errors; still load whatever is online
      }
      load();
    })();
  }, [load]);

  const inNow = members.filter((m) => m.status === "in");

  const submit = async () => {
    if (!form.name.trim() || !form.dob || !form.phone.trim() || !form.email.trim()) {
      toast.error("Name, date of birth, email and phone are required");
      return;
    }
    setBusy(true);
    try {
      const m = await addMember(form);
      setLastAdded(m);
      setForm({ name: "", dob: "", email: "", phone: "", parentPhone: "" });
      toast.success(`Member created — ${m.number}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create member");
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Delete membership for ${m.name}? This also removes their visit history.`)) return;
    try {
      await deleteMember(m.id);
      await load();
    } catch (e) {
      toast.error("Could not delete member");
    }
  };

  const filtered = members.filter((m) =>
    `${m.name} ${m.number} ${m.phone} ${m.email}`.toLowerCase().includes(query.toLowerCase())
  );

  const memberUrl = `${window.location.origin}/door`;
  const inputCls = "w-full h-14 rounded-xl bg-[#09090B] border border-white/10 px-4 text-white text-lg outline-none focus:border-[#FF007F]";

  return (
    <div className="min-h-screen bg-[#09090B] p-4 md:p-6">
      <AdminHeader title="MEMBERSHIPS" accent="#FF007F" />

      <div data-testid="online-note" className="mb-5 rounded-2xl border border-[#00FA9A]/30 bg-[#00FA9A]/10 text-[#00FA9A] px-5 py-3 font-semibold text-sm">
        Stored online — the directory and door sign-ins are visible on every device.
      </div>

      {/* Tabs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`h-16 rounded-xl font-display font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform duration-75 ${
              tab === t.id ? "bg-[#FF007F] text-white" : "bg-[#18181B] text-white border border-white/10"
            }`}>
            <t.icon className="w-5 h-5" /> {t.label}
          </button>
        ))}
      </div>

      {/* SIGN UP */}
      {tab === "signup" && (
        <div className="max-w-2xl">
          <div className="bg-[#18181B] border border-white/10 rounded-3xl p-6">
            <p className="font-display font-black text-2xl mb-1">New Youth Member</p>
            <p className="text-[#71717A] mb-6">Fill this in with the young person at the bar.</p>
            <div className="grid gap-4">
              <label className="block">
                <span className="block text-[#A1A1AA] font-bold text-xs tracking-widest uppercase mb-1">Full name *</span>
                <input data-testid="signup-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Alex Smith" />
              </label>
              <label className="block">
                <span className="block text-[#A1A1AA] font-bold text-xs tracking-widest uppercase mb-1">Date of birth *</span>
                <input data-testid="signup-dob" type="date" className={inputCls} value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} />
              </label>
              <label className="block">
                <span className="block text-[#A1A1AA] font-bold text-xs tracking-widest uppercase mb-1">Email *</span>
                <input data-testid="signup-email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="alex@example.com" />
              </label>
              <label className="block">
                <span className="block text-[#A1A1AA] font-bold text-xs tracking-widest uppercase mb-1">Phone *</span>
                <input data-testid="signup-phone" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="07..." />
              </label>
              <label className="block">
                <span className="block text-[#A1A1AA] font-bold text-xs tracking-widest uppercase mb-1">Parent / guardian number (optional)</span>
                <input data-testid="signup-parent" className={inputCls} value={form.parentPhone} onChange={(e) => setForm({ ...form, parentPhone: e.target.value })} placeholder="Optional" />
              </label>
              <button data-testid="signup-submit" onClick={submit} disabled={busy}
                className="h-16 rounded-2xl bg-[#FF007F] text-white font-display font-black text-2xl flex items-center justify-center gap-3 active:scale-95 transition-transform duration-100 mt-2 disabled:opacity-50">
                <UserPlus className="w-7 h-7" /> {busy ? "SAVING…" : "CREATE MEMBERSHIP"}
              </button>
            </div>
          </div>
          {lastAdded && (
            <div data-testid="signup-success" className="mt-4 rounded-2xl bg-[#00FA9A]/15 border border-[#00FA9A] text-[#00FA9A] px-5 py-4 font-bold">
              ✓ {lastAdded.name} is now a YAZ member — card number <span className="font-mono">{lastAdded.number}</span>.
              They can sign in at the door with their name &amp; date of birth.
            </div>
          )}
        </div>
      )}

      {/* DIRECTORY */}
      {tab === "directory" && (
        <div>
          <div className="relative max-w-md mb-4">
            <Search className="w-5 h-5 text-[#71717A] absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="directory-search" className={inputCls + " pl-10"} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, number, phone…" />
          </div>
          <div className="grid gap-3">
            {filtered.length === 0 && <p className="text-[#71717A]">No members{members.length ? " match your search" : " yet — add one in SIGN UP"}.</p>}
            {filtered.map((m) => {
              const inside = m.status === "in";
              const strikes = m.strikes || 0;
              return (
                <div key={m.id} data-testid={`member-row-${m.id}`} onClick={() => openMember(m)}
                  className="bg-[#18181B] border border-white/10 rounded-2xl p-4 flex items-center justify-between gap-4 cursor-pointer hover:border-[#FF007F]/60 active:scale-[0.99] transition-all duration-75">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-display font-black text-xl text-white">{m.name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${inside ? "bg-[#00FA9A] text-black" : "bg-[#27272A] text-[#A1A1AA]"}`}>{inside ? "IN" : "OUT"}</span>
                      {strikes > 0 && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-[#FF3B30]/20 text-[#FF6B60] flex items-center gap-1">
                          <AlertTriangle className="w-3.5 h-3.5" /> {strikes} STRIKE{strikes > 1 ? "S" : ""}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-[#71717A] mt-0.5">
                      <span className="font-mono">{m.number}</span> · DOB {m.dob} · {m.phone}{m.email ? ` · ${m.email}` : ""}{m.parent_phone ? ` · Parent ${m.parent_phone}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`member-delete-${m.id}`} onClick={(e) => { e.stopPropagation(); removeMember(m); }}
                      className="w-10 h-10 rounded-lg bg-[#27272A] hover:bg-[#FF3B30] text-white flex items-center justify-center active:scale-95 transition-transform duration-75">
                      <Trash2 className="w-5 h-5" />
                    </button>
                    <ChevronRight className="w-6 h-6 text-[#52525B]" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* WHO'S IN / attendance */}
      {tab === "attendance" && (
        <div>
          <div className="bg-[#18181B] border border-white/10 rounded-2xl p-5 mb-5">
            <p className="font-display font-bold text-sm tracking-widest text-[#A1A1AA]">CURRENTLY IN THE BUILDING <span className="text-[#52525B] normal-case tracking-normal">· resets at 12pm daily</span></p>
            <p data-testid="in-count" className="font-display font-black text-5xl text-[#00FA9A] mt-1">{inNow.length}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {inNow.map((m) => <span key={m.id} className="px-3 py-1 rounded-full bg-[#00FA9A]/15 border border-[#00FA9A]/40 text-[#00FA9A] text-sm font-bold">{m.name}</span>)}
              {inNow.length === 0 && <span className="text-[#71717A]">Nobody signed in right now.</span>}
            </div>
          </div>
          <WeeklyAttendance onChange={load} />
        </div>
      )}

      {/* RAVE LIST (event registrations) */}
      {tab === "events" && (
        <div>
          <div className="rounded-3xl p-6 bg-gradient-to-br from-[#7C3AED] via-[#FF007F] to-[#FF6B00] mb-5">
            <div className="flex items-center gap-3">
              <Ghost className="w-9 h-9 text-white" />
              <div>
                <p className="font-display font-black text-2xl leading-none">{HALLOWEEN.title}</p>
                <p className="text-white/85 font-bold text-sm mt-1">{HALLOWEEN.dateLabel}</p>
              </div>
            </div>
            <p data-testid="event-count" className="font-display font-black text-5xl mt-4">{eventRegs.reduce((s, r) => s + (r.tickets || 1), 0)} <span className="text-xl font-bold text-white/80">/ {HALLOWEEN.capacity} tickets</span></p>
          </div>
          <div className="bg-[#18181B] border border-white/10 rounded-2xl divide-y divide-white/5">
            {eventRegs.length === 0 && <div className="px-4 py-8 text-center text-[#71717A]">Nobody booked yet.</div>}
            {eventRegs.map((r, i) => (
              <div key={r.id} data-testid={`event-reg-${r.id}`} className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-[#52525B] font-mono text-sm w-6 text-right">{i + 1}</span>
                  <span className="font-bold text-white">{r.member_name}</span>
                  <span className="text-[#71717A] font-mono text-xs">{r.number || r.phone || ""}</span>
                  <span className="text-white font-bold text-sm">×{r.tickets || 1}</span>
                  <span data-testid={`event-reg-pay-${r.id}`} className={`px-2 py-0.5 rounded-full text-xs font-bold ${r.paid ? "bg-[#00FA9A]/15 text-[#00FA9A]" : "bg-[#FFB800]/15 text-[#FFB800]"}`}>{r.paid ? "PAID ONLINE" : `PAY £${(r.tickets || 1) * HALLOWEEN.priceValue} ON ARRIVAL`}</span>
                </div>
                <span className="text-[#A1A1AA] text-sm">{new Date(r.at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* DOOR QR */}
      {tab === "qr" && (
        <div className="max-w-md">
          <div className="bg-white rounded-3xl p-8 flex flex-col items-center" id="door-qr-print">
            <QRCodeCanvas value={memberUrl} size={260} level="M" includeMargin />
            <p className="text-black font-display font-black text-2xl mt-4">SCAN TO SIGN IN / OUT</p>
            <p className="text-black/60 font-bold mt-1">YAZ Workington Membership</p>
          </div>
          <p className="text-[#71717A] text-sm mt-4 break-all">Links to: {memberUrl}</p>
          <button data-testid="print-qr" onClick={() => window.print()}
            className="mt-4 h-14 px-6 rounded-xl bg-[#18181B] border border-white/10 text-white font-display font-bold flex items-center gap-2 active:scale-95 transition-transform duration-75">
            <Printer className="w-5 h-5" /> PRINT QR
          </button>
        </div>
      )}

      {selected && edit && (
        <div data-testid="member-detail-modal" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={closeMember}>
          <div className="bg-[#18181B] border border-white/10 rounded-3xl p-6 w-full max-w-xl my-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-5">
              <div>
                <p className="font-display font-black text-3xl text-white">{selected.name}</p>
                <p className="text-[#71717A] font-mono mt-1">{selected.number} · {selected.status === "in" ? "Currently IN" : "Signed out"}</p>
              </div>
              <button data-testid="close-member" onClick={closeMember} className="w-10 h-10 rounded-lg bg-[#27272A] text-white flex items-center justify-center"><X className="w-5 h-5" /></button>
            </div>

            {/* Strikes */}
            <div className="rounded-2xl border border-[#FF3B30]/30 bg-[#FF3B30]/5 p-4 mb-5">
              <div className="flex items-center justify-between">
                <span className="font-display font-bold text-sm tracking-widest text-[#FF6B60] flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> STRIKES</span>
                <div className="flex items-center gap-2">
                  {[0, 1].map((i) => (
                    <span key={i} className={`w-6 h-6 rounded-full border-2 ${i < (edit.strikes || 0) ? "bg-[#FF3B30] border-[#FF3B30]" : "border-white/20"}`} />
                  ))}
                </div>
              </div>
              <div className="flex gap-3 mt-3">
                <button data-testid="strike-remove" onClick={() => setStrikes((edit.strikes || 0) - 1)} disabled={(edit.strikes || 0) <= 0}
                  className="flex-1 h-12 rounded-xl bg-[#27272A] text-white font-bold disabled:opacity-40">− REMOVE</button>
                <span data-testid="strikes-count" className="w-16 text-center font-display font-black text-2xl text-white leading-[3rem]">{edit.strikes || 0}/2</span>
                <button data-testid="strike-add" onClick={() => setStrikes((edit.strikes || 0) + 1)} disabled={(edit.strikes || 0) >= 2}
                  className="flex-1 h-12 rounded-xl bg-[#FF3B30] text-white font-bold disabled:opacity-40">+ ADD STRIKE</button>
              </div>
            </div>

            {/* Edit fields */}
            <div className="grid gap-4">
              <label className="block">
                <span className="block text-[#A1A1AA] font-bold text-xs tracking-widest uppercase mb-1">Full name</span>
                <input data-testid="edit-name" className={inputCls} value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
              </label>
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-[#A1A1AA] font-bold text-xs tracking-widest uppercase mb-1">Date of birth</span>
                  <input data-testid="edit-dob" type="date" className={inputCls} value={edit.dob || ""} onChange={(e) => setEdit({ ...edit, dob: e.target.value })} />
                </label>
                <label className="block">
                  <span className="block text-[#A1A1AA] font-bold text-xs tracking-widest uppercase mb-1">Phone</span>
                  <input data-testid="edit-phone" className={inputCls} value={edit.phone || ""} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} />
                </label>
              </div>
              <label className="block">
                <span className="block text-[#A1A1AA] font-bold text-xs tracking-widest uppercase mb-1">Email</span>
                <input data-testid="edit-email" className={inputCls} value={edit.email || ""} onChange={(e) => setEdit({ ...edit, email: e.target.value })} />
              </label>
              <label className="block">
                <span className="block text-[#A1A1AA] font-bold text-xs tracking-widest uppercase mb-1">Parent / guardian number</span>
                <input data-testid="edit-parent" className={inputCls} value={edit.parentPhone || ""} onChange={(e) => setEdit({ ...edit, parentPhone: e.target.value })} placeholder="Optional" />
              </label>
              <label className="block">
                <span className="block text-[#A1A1AA] font-bold text-xs tracking-widest uppercase mb-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Address</span>
                <textarea data-testid="edit-address" rows={2} className={inputCls + " h-auto py-3 resize-none"} value={edit.address || ""} onChange={(e) => setEdit({ ...edit, address: e.target.value })} placeholder="Add in person — leave blank until collected" />
              </label>
            </div>

            <div className="flex gap-3 mt-6">
              <button data-testid="modal-delete-member" onClick={() => { removeMember(selected); closeMember(); }}
                className="h-14 px-5 rounded-xl bg-[#27272A] hover:bg-[#FF3B30] text-white font-bold flex items-center gap-2"><Trash2 className="w-5 h-5" /> Delete</button>
              <button data-testid="save-member" onClick={saveMember}
                className="flex-1 h-14 rounded-xl bg-[#FF007F] text-white font-display font-black text-xl flex items-center justify-center gap-2 active:scale-95 transition-transform duration-100">
                <Save className="w-6 h-6" /> SAVE CHANGES
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
